import { Injectable, Signal, signal } from '@angular/core';

/**
 * Shape of the vehicle/deal context consumed and displayed by the AI chatbot.
 * All fields are optional because the chatbot may receive partial data.
 */
export interface VehicleData {
  year?: number | string;
  make?: string;
  model?: string;
  salesPrice?: number;
  cashDown?: number;
  netTrade?: number;
  previousSalesPrice?: number;
  previousCashDown?: number;
  previousNetTrade?: number;
}

/**
 * Partial field-level updates emitted by the chatbot to the host application.
 * Keys are field names; values are the new values expressed as strings
 * (the host is responsible for coercing to the correct primitive type).
 */
export type VehicleDataUpdate = Record<string, string>;

/**
 * Lightweight service that shares vehicle/deal context between the shell-hosted
 * AI chatbot component and the active MFE route component.
 *
 * State is held in Angular signals (not RxJS Subjects) because this is purely
 * in-memory local state with no HTTP involvement.  The callback pattern is used
 * for the update direction (chatbot → MFE) so that the MFE can apply field
 * changes without any Observable subscription.
 */
@Injectable({
  providedIn: 'root'
})
export class ChatbotDataService {
  /** Registered handler for chatbot-initiated field updates; null when no MFE is active. */
  private currentCallback: ((data: VehicleDataUpdate) => void) | null = null;

  /**
   * Writable signal holding the current vehicle/deal context.
   * Private so only this service can mutate state; consumers read via `vehicleData`.
   */
  private readonly _vehicleData = signal<VehicleData | null>(null);

  /**
   * Readonly signal of the current vehicle/deal context.
   * Components read this signal directly; Angular tracks the dependency and
   * re-evaluates derived computations or effects when the value changes.
   */
  readonly vehicleData: Signal<VehicleData | null> = this._vehicleData.asReadonly();

  /**
   * Get current vehicle data synchronously.
   * Equivalent to reading `vehicleData()` from outside an injection context.
   */
  getCurrentVehicleData(): VehicleData | null {
    return this._vehicleData();
  }

  /**
   * Set current vehicle data (called by the active route component when deal data changes).
   */
  setVehicleData(data: VehicleData): void {
    console.log('[ChatbotData] Setting vehicle data:', data);
    this._vehicleData.set(data);
  }

  /**
   * Register a callback to handle vehicle data updates (called by the route component on init).
   */
  registerCallback(callback: (data: VehicleDataUpdate) => void): void {
    console.log('[ChatbotData] Registering callback');
    this.currentCallback = callback;
  }

  /**
   * Unregister the callback and clear the vehicle data signal (called on route component destroy).
   */
  unregisterCallback(): void {
    console.log('[ChatbotData] Unregistering callback');
    this.currentCallback = null;
    this._vehicleData.set(null);
  }

  /**
   * Dispatch vehicle data updates from the chatbot to the active route component callback.
   */
  emitUpdate(data: VehicleDataUpdate): void {
    console.log('[ChatbotData] Emitting update:', data);

    if (this.currentCallback) {
      this.currentCallback(data);
    } else {
      console.warn('[ChatbotData] No callback registered to handle update');
    }
  }

  /**
   * Returns true when a route component has registered a callback.
   */
  hasCallback(): boolean {
    return !!this.currentCallback;
  }
}
